import {
  fetchNotificationsSocket,
  markNotificationAsReadSocket,
  markNotificationAsViewedSocket,
  markAllNotificationsAsReadSocket,
  onNotificationsResponse,
  onNotificationRead,
  onNotificationViewed,
  onNotificationReadAll
} from './socketService';

const SOCKET_TIMEOUT = 10000;

export const isNotificationUnviewed = (notification = {}) => {
  const seen = notification.is_seen;
  if (seen === 0 || seen === '0') return true;
  if (seen === 1 || seen === '1') return false;
  if (typeof seen === 'boolean') return !seen;
  if (typeof notification.viewed === 'boolean') return !notification.viewed;
  return !notification.viewed_at && !notification.is_viewed;
};

export const isNotificationUnread = (notification = {}) => {
  if (typeof notification.read === 'boolean') return !notification.read;
  return !notification.read_at;
};

export const normalizeCounts = (counts = {}, notifications = []) => {
  const normalized = {
    all: Number(counts.all) || 0,
    unread: Number(counts.unread) || 0,
    read: Number(counts.read) || 0,
    unviewed: Number(counts.unviewed ?? counts.unseen) || 0
  };

  if (notifications.length) {
    const unviewedFromItems = notifications.filter(isNotificationUnviewed).length;
    const unreadFromItems = notifications.filter(isNotificationUnread).length;

    if (unviewedFromItems > normalized.unviewed) {
      normalized.unviewed = unviewedFromItems;
    }
    if (unreadFromItems > normalized.unread) {
      normalized.unread = unreadFromItems;
    }
  }

  return normalized;
};

const resolveNotificationType = (notification) => {
  const category = notification.category;
  if (category === 'poi_submission' || category === 'poi_image_upload') {
    return 'approval';
  }
  if (category) return category;
  if (notification.type && Number.isNaN(Number(notification.type))) {
    return notification.type;
  }
  return 'alert';
};

export const normalizeNotification = (notification = {}) => ({
  ...notification,
  type: resolveNotificationType(notification),
  read:
    typeof notification.read === 'boolean'
      ? notification.read
      : Boolean(notification.read_at),
  viewed:
    typeof notification.viewed === 'boolean'
      ? notification.viewed
      : notification.is_seen === 0 || notification.is_seen === '0'
        ? false
        : notification.is_seen === 1 || notification.is_seen === '1'
          ? true
          : typeof notification.is_seen === 'boolean'
            ? notification.is_seen
            : Boolean(notification.is_viewed || notification.viewed_at),
  createdAt:
    notification.createdAt ||
    notification.created_at ||
    notification.updatedAt ||
    notification.updated_at ||
    new Date().toISOString()
});

const normalizeResponse = (data) => {
  console.log('notificationService: Raw socket response before normalization:', data);
  const items = Array.isArray(data?.data) ? data.data : [];
  const normalized = {
    ...data,
    data: items.map(normalizeNotification)
  };
  console.log('notificationService: Normalized socket response:', normalized);
  return normalized;
};

const withSocketResponse = (emitFn, listenFn, timeout = SOCKET_TIMEOUT) =>
  new Promise((resolve, reject) => {
    let unsubscribe;
    const timeoutId = setTimeout(() => {
      if (unsubscribe) unsubscribe();
      reject(new Error('Socket request timed out'));
    }, timeout);

    const handleResponse = (data) => {
      clearTimeout(timeoutId);
      if (unsubscribe) unsubscribe();
      if (data?.result === false) {
        reject(new Error(data?.message || 'Request failed'));
        return;
      }
      resolve(data);
    };

    unsubscribe = listenFn(handleResponse);
    emitFn();
  });

export const getNotifications = (limit = 10, page = 1) => {
  const offset = (page - 1) * limit;
  return withSocketResponse(
    () => fetchNotificationsSocket(limit, offset),
    onNotificationsResponse
  ).then(normalizeResponse);
};

export const getRecentNotifications = (limit = 10) =>
  getNotifications(limit, 1);

export const markAsRead = (notificationId) =>
  withSocketResponse(
    () => markNotificationAsReadSocket(notificationId),
    onNotificationRead,
    5000
  );

export const markAsViewed = () =>
  withSocketResponse(
    () => markNotificationAsViewedSocket(),
    onNotificationViewed,
    5000
  );

export const markAllAsRead = () =>
  withSocketResponse(
    () => markAllNotificationsAsReadSocket(),
    onNotificationReadAll,
    5000
  );
